import React, {createContext, useContext, useState, useEffect, useCallback} from 'react';
import AsyncStorage from '@react-native-community/async-storage';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';

import {setUser} from '../../store/auth/authActions';
import UsersService from '../../services/UsersService';
import {lightTheme, darkTheme} from './theme';

const DarkModeContext = createContext({
    isDarkMode: false,
    theme: lightTheme,
    toggleDarkMode: () => {},
});

export function useDarkMode() {
    return useContext(DarkModeContext);
}

function DarkModeProviderInner({children, auth, setUser}) {
    const userPreferenceDarkMode = auth.user?.user_preference_dark_mode;
    const [isDarkMode, setIsDarkMode] = useState(() => {
        return Number(userPreferenceDarkMode) === 1;
    });

    useEffect(() => {
        if (userPreferenceDarkMode !== undefined) {
            setIsDarkMode(Number(userPreferenceDarkMode) === 1);
        }
    }, [userPreferenceDarkMode]);

    useEffect(() => {
        // Also check AsyncStorage for immediate local state
        AsyncStorage.getItem('messageFlowDarkMode').then(value => {
            if (value !== null && userPreferenceDarkMode === undefined) {
                setIsDarkMode(value === '1');
            }
        });
    }, [userPreferenceDarkMode]);

    const toggleDarkMode = useCallback(async () => {
        const newValue = !isDarkMode;
        const userPayload = auth.user ? {
            ...auth.user,
            user_preference_dark_mode: newValue ? 1 : 0,
        } : null;

        setIsDarkMode(newValue);
        if (userPayload) {
            // Keep global UI (like MainNavigator sidebar) in sync immediately.
            setUser(userPayload);
        }

        await AsyncStorage.setItem('messageFlowDarkMode', newValue ? '1' : '0');

        const organizationId = auth.organization?.organization_id;
        const userId = auth.user?.user_id;
        if (organizationId && userId && userPayload) {
            try {
                const updatedUser = await UsersService.updateUser(organizationId, userId, {
                    user_preference_dark_mode: newValue ? 1 : 0,
                }, {
                    suppressNotification: true,
                });
                if (updatedUser) {
                    setUser(updatedUser);
                }
            } catch (e) {
                console.log('Error saving dark mode preference:', e);
            }
        }
    }, [isDarkMode, auth.organization, auth.user, setUser]);

    const theme = isDarkMode ? darkTheme : lightTheme;

    return (
        <DarkModeContext.Provider value={{isDarkMode, theme, toggleDarkMode}}>
            {children}
        </DarkModeContext.Provider>
    );
}

const mapStateToProps = (state) => ({auth: state.auth});
const mapDispatchToProps = dispatch => bindActionCreators({setUser}, dispatch);

export const DarkModeProvider = connect(mapStateToProps, mapDispatchToProps)(DarkModeProviderInner);
